<template>
  <div v-bind="$attrs">
    <NuxtLink v-if="imageLink" :to="imageLink">
      <NuxtImg
        :src="getImageUrl()"
        :alt="banner.image?.alt ?? ''"
        class="w-full object-cover"
        :style="{
          filter: banner.image?.brightness ? 'brightness(' + (banner.image?.brightness ?? 1) + ')' : '',
          height: getImageHeight(),
        }"
        :loading="props.lazyLoading"
        :data-testid="'banner-image-' + meta.uuid"
      />
    </NuxtLink>

    <NuxtImg
      v-else
      :src="getImageUrl()"
      :alt="banner.image?.alt ?? ''"
      class="w-full object-cover"
      :style="{
        filter: banner.image?.brightness ? 'brightness(' + (banner.image?.brightness ?? 1) + ')' : '',
        height: getImageHeight(),
      }"
      :loading="props.lazyLoading"
      :data-testid="'banner-image-' + meta.uuid"
    />

    <div
      v-if="banner.text && !imageLink"
      :class="['absolute inset-0 p-4 flex flex-col md:basis-2/4', { 'md:p-10': banner.text.bgcolor }]"
      :style="{
        color: banner.text.color,
        textAlign: getTextAlignment(rteAlignment ?? ''),
        alignItems: getContentPosition(banner.text.align ?? ''),
        justifyContent: getContentPosition(banner.text.justify ?? ''),
      }"
      :data-testid="'banner-overlay-' + meta.uuid"
    >
      <div
        :class="bannerContentClass"
        :style="{
          backgroundColor: banner.text.background ? hexToRgba(banner.text.bgcolor, banner.text.bgopacity) : '',
        }"
        :data-testid="'banner-content-' + meta.uuid"
      >
        <TextContent :text="banner.text" :index="props.slideIndex" />
        <UiButton
          v-if="banner.button && banner.button.label && banner.button.link"
          class="flex flex-col md:flex-row gap-4 mt-6"
          :tag="NuxtLink"
          :to="localePath(banner.button.link ?? '')"
          :variant="banner.button.variant ?? 'primary'"
          size="lg"
          :data-testid="'banner-button-' + meta.uuid"
        >
          {{ banner.button.label }}
        </UiButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BannerProps } from './types';

const NuxtLink = resolveComponent('NuxtLink');

const localePath = useLocalePath();

const viewport = useViewport();
const isMobile = computed(() => viewport.isLessThan('lg'));

const props = defineProps<BannerProps & { slideIndex?: number }>();

const banner = computed(() => props.content);
const { hexToRgba, getImageHeight, getTextAlignment, getContentPosition } = useBlockContentHelper();

const rteAlignment = computed(() => banner.value.button?.alignment);
const imageLink = computed(() => {
  const link = banner.value.image?.link?.trim();
  return link ? localePath(link) : '';
});

const getImageUrl = () => {
  switch (viewport.breakpoint.value) {
    case '4xl': {
      return banner.value.image?.wideScreen ?? '';
    }
    case 'lg': {
      return banner.value.image?.desktop ?? '';
    }
    case 'md': {
      return banner.value.image?.tablet ?? '';
    }
    default: {
      return banner.value.image?.mobile ?? '';
    }
  }
};

const bannerContentClass = computed(() => {
  return isMobile.value ? 'p-4 md:p-6 rounded-lg w-full' : 'p-4 md:p-6 rounded-lg md:max-w-[50%] mx-5';
});
</script>
